import { useState } from "react";
import { PLANS } from "@/components/market/Plans";
import { isFramed, openTopLevel } from "@/lib/topNavigation";
import { toast } from "sonner";
import { AlertTriangle, BadgeCheck, CalendarClock, CreditCard, Crown, Loader2 } from "lucide-react";
import { UnsubscribedNotice, UnsubscribeFlow } from "@/components/dev/UnsubscribeFlow";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { CustomOfferPay } from "@/components/messages/CustomOfferPay";
import { CurrencyToggle, usePrice } from "@/components/market/Plans";
import { FxNote } from "@/components/market/FxNote";
import { OfferCard } from "@/components/offers/OfferCard";
import { Link } from "react-router-dom";
import { useDevAuth } from "@/context/DevAuthContext";
import { PrepaidCheckout } from "@/components/market/Prepaid";

// Unicode isolates (= <bdi>): Latin plan names, counts and prices keep their order inside RTL sentences.
const iso = (s) => `\u2068${s}\u2069`;

const STYLE = {
  active: "bg-emerald-600 text-white",
  late: "bg-amber-500 text-black",
  suspended: "bg-red-700 text-white",
  never_paid: "border border-neutral-500 text-neutral-200",
  vip: "bg-gradient-to-r from-amber-300 to-amber-500 text-black",
};

export const StatusBadge = ({ status, testId = "dev-status-badge" }) => {
  const { t } = useI18n();
  return <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${STYLE[status]}`} data-testid={testId}>{t(`status.${status}`)}</span>;
};

const PlanPicker = ({ billing, onChange }) => {
  const { t, fmtDate, locale } = useI18n();
  const { dev } = useDevAuth();
  const [busy, setBusy] = useState(false);
  const change = async (plan) => {
    setBusy(true);
    try {
      const { data } = await api.put("/dev/billing/plan", { plan });
      onChange(data);
      toast.success(t("plans.changed"));
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  const current = billing.plan === "custom" ? { id: "custom", name: t("plans.customName", { n: new Intl.NumberFormat(locale).format(billing.max_apps) }), apps: billing.max_apps } : PLANS.find((p) => p.id === billing.plan) || PLANS[0];
  const n = (x) => new Intl.NumberFormat(locale).format(x);
  const { price, k } = usePrice(billing.currency || undefined);
  const off = billing.payments_available === false;
  const cancelling = billing.status === "active" && billing.cancel_at_period_end; // only "Me réabonner" while a cancellation is pending
  const mini = billing.kind === "minisite";
  return (
    <div className="mt-4 space-y-2 rounded-xl border border-[rgba(245,197,66,0.2)] p-3" data-testid="plan-picker">
      {!mini && <p className="text-sm text-white" data-testid="plan-current">{t(billing.status === "vip" ? "plans.vip" : billing.plan === "custom" ? "plans.currentCustom" : "plans.current", { name: iso(current.name), n: iso(n(billing.max_apps || current.apps)) })}</p>}
      {!mini && dev && <p className="break-words text-xs text-neutral-300" data-testid="plan-apps-used">{t("plans.appsMiniUsed", { used: n(dev.apps_count || 0), max: n(billing.max_apps || current.apps), m: n(dev.minisites_count || 0) })}</p>}
      {mini && billing.terms && <p className="break-words text-sm text-white" data-testid="plan-current">{t(billing.terms.mode === "prepaid" ? "client.prepaidPlan" : "client.recurringPlan", { n: iso(billing.terms.interval) })}</p>}
      {billing.pending_plan && <p className="text-xs text-amber-200" data-testid="plan-pending">{t("plans.pending", { name: PLANS.find((p) => p.id === billing.pending_plan)?.name, date: fmtDate(billing.pending_from) })}</p>}
      {billing.custom_offer?.flex && billing.status !== "vip" && (
        <OfferCard a={{ ...billing.custom_offer, offer_id: billing.custom_offer.id }} onPaid={() => api.get("/dev/billing").then(({ data }) => onChange(data))} testId="billing-offer-card" />
      )}
      {mini && !billing.custom_offer && billing.status !== "vip" && (
        <Link to="/espace-developpeur/messages?nouveau=custom" className="btn-gold btn-sm max-w-full whitespace-normal text-start" data-testid="billing-request-plan">{t("client.requestPlan")}</Link>
      )}
      {billing.custom_offer && !billing.custom_offer.flex && billing.status !== "vip" && !cancelling && !(billing.plan === "custom" && billing.max_apps === billing.custom_offer.apps) && (
        <div className="rounded-lg border border-[var(--gold)] p-2" data-testid="billing-custom-offer">
          <p className="text-xs text-amber-100">{t(k("custom.offer"), { n: iso(n(billing.custom_offer.apps)), price: iso(price(billing.custom_offer)) })}</p>
          <CustomOfferPay offer={billing.custom_offer} onPaid={onChange} disabled={off} forced={billing.currency || undefined} testId="billing-custom-pay" />
        </div>
      )}
      {billing.status !== "vip" && !cancelling && !mini && (
        <div className="flex flex-wrap gap-2">
          {PLANS.filter((p) => p.id !== current.id).map((p) => (
            <button key={p.id} type="button" disabled={busy || off} onClick={() => change(p.id)} className="btn-red btn-sm h-auto max-w-full !whitespace-normal text-start leading-snug" data-testid={`plan-switch-${p.id}`}>
              {t(k("plans.change"), { name: iso(p.name), price: iso(price(p)), n: iso(n(p.apps)) })}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const BillingCard = ({ billing, onChange }) => {
  const { t, fmtDate } = useI18n();
  const [busy, setBusy] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const { cur, price, k } = usePrice(billing.currency || undefined);
  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dev/billing/checkout", { origin_url: window.location.origin, currency: cur });
      if (isFramed()) setCheckoutUrl(data.checkout_url);
      openTopLevel(data.checkout_url);
    } catch (err) {
      toast.error(translateError(err, t));
      setBusy(false);
    }
  };
  const { status } = billing;
  const off = billing.payments_available === false;
  const prepaidSoon = billing.terms?.mode === "prepaid" && status === "active" && billing.period_end && new Date(billing.period_end) - Date.now() < 14 * 86400000;
  const mini = billing.kind === "minisite";
  return (
    <section className="card-carbon rounded-2xl p-6" data-testid="billing-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><CreditCard className="h-5 w-5 text-[var(--gold)]" /> {t("billing.title")}</h2>
        <div className="flex flex-wrap items-center gap-2">
          {!billing.currency && status !== "vip" && <CurrencyToggle testId="billing-currency-toggle" />}
          <StatusBadge status={status} />
        </div>
      </div>
      <FxNote show={cur === "usd" && status !== "vip"} className="mt-3" testId="billing-fx-note" />
      {billing.period_end && status !== "vip" && (
        <p className="mt-3 flex items-center gap-2 text-sm text-neutral-300" data-testid="billing-period-end">
          <CalendarClock className="h-4 w-4 text-[var(--gold)]" /> {t(billing.cancel_at_period_end ? "billing.endsOn" : "billing.paidUntil", { date: fmtDate(billing.period_end) })}
        </p>
      )}
      {off && status !== "vip" && (
        <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-400/60 bg-amber-950/50 px-4 py-3 text-sm text-amber-100" role="status" data-testid="billing-payments-unavailable">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t("billing.unavailable")}
        </p>
      )}
      <PlanPicker billing={billing} onChange={onChange} />
      {prepaidSoon && (
        <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-400/60 bg-amber-950/50 px-4 py-3 text-sm text-amber-100" role="status" data-testid="billing-prepaid-reminder">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t("billing.prepaidSoon", { date: iso(fmtDate(billing.period_end)) })}
        </p>
      )}
      {billing.terms?.mode === "prepaid" && status === "active" && !billing.has_subscription && <p className="mt-2 text-xs text-neutral-400" data-testid="billing-prepaid-note">{t("billing.prepaidNote")}</p>}
      {status === "late" && (
        <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-400/60 bg-amber-950/50 px-4 py-3 text-sm font-semibold text-amber-100" role="alert" data-testid="billing-late-notice">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t(billing.vip_removed ? "billing.vipLate" : "billing.late", { n: billing.days_left })}
        </p>
      )}
      {status === "late" && billing.payment_reminder && (
        <div className="mt-3 min-w-0 space-y-2 rounded-xl border border-amber-400/40 bg-amber-950/30 px-4 py-3" data-testid="billing-payment-reminder">
          <p className="break-words text-sm font-bold text-amber-100">{billing.payment_reminder.title}</p>
          <p className="break-words text-sm text-amber-50/90">{billing.payment_reminder.message}</p>
          <button type="button" className="btn-gold btn-sm min-h-[44px] max-w-full whitespace-normal" onClick={() => openTopLevel(billing.payment_reminder.pay_url)} data-testid="billing-retry-payment">
            <CreditCard className="h-4 w-4 shrink-0" /> {billing.payment_reminder.button}</button>
        </div>
      )}
      {status === "suspended" && (
        <p className="mt-3 flex items-start gap-2 rounded-xl border border-red-500/60 bg-red-950/60 px-4 py-3 text-sm text-red-100" role="alert" data-testid="billing-suspended-notice">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t(billing.by_admin ? "billing.suspendedAdmin" : "billing.suspended")}
        </p>
      )}
      {status === "never_paid" && <p className="mt-3 text-sm text-neutral-400" data-testid="billing-never-paid">{t(mini ? (billing.trial?.status === "trialing" ? "trial.billingNote" : "billing.neverPaidMini") : "billing.neverPaid")}</p>}
      {status === "vip" && <p className="mt-3 flex items-center gap-2 font-semibold text-amber-200" data-testid="billing-vip-notice"><Crown className="h-4 w-4" /> {t("billing.vip")}</p>}
      {status === "active" && <p className="mt-3 flex items-center gap-2 text-sm text-emerald-200"><BadgeCheck className="h-4 w-4" /> {t(mini ? "billing.activeHintMini" : "billing.activeHint")}</p>}
      {!["active", "vip"].includes(status) && !billing.by_admin && !mini && !billing.custom_offer?.flex && (
        <button type="button" className="btn-gold btn-cta mt-5 w-full sm:w-auto" onClick={subscribe} disabled={busy || off} data-testid="billing-subscribe-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {t(k("billing.subscribe"), { price: iso(price(PLANS.find((p) => p.id === billing.plan) || PLANS[0])) })}
        </button>
      )}
      {!mini && !billing.by_admin && status !== "vip" && billing.plan !== "custom" && !billing.custom_offer?.flex && !billing.terms && (
        <div className="mt-5"><PrepaidCheckout billing={billing} testId="billing-prepaid" /></div>
      )}
      {checkoutUrl && (
        <a href={checkoutUrl} target="_top" rel="noopener" className="btn-red btn-sm btn-cta mt-3 w-full sm:w-auto" data-testid="billing-open-checkout-link">
          {t("billing.openCheckout")}
        </a>
      )}
      {billing.has_subscription && status === "active" && (billing.cancel_at_period_end
        ? <UnsubscribedNotice billing={billing} onChange={onChange} />
        : <UnsubscribeFlow billing={billing} onChange={onChange} />)}
    </section>
  );
};
